import AppToast from "@toothfairy/shared-ui/AppToast";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import requestLogsHelper from "../../../API/RequestLogs/requestLogsHelper";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";
import useSections from "../../../Hooks/useSections";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import SupportEditScreen from "../Components/SupportEditScreen";

const SupportEditContainer = () => {
  const { displayedOpt: log, setDisplayedView } = useSections();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { getActiveWorkspace, updateWorkspaceInContext } = useWorkspaces();
  const workspace = getActiveWorkspace();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalUsedHours, setTotalUsedHours] = useState(
    workspace?.supportHoursUsed || 0
  );

  // Support hours are a budget: validate against every support log associated
  // with the workspace, not only the hours logged within the billing cycle.
  const loadTotalUsedHours = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      const result =
        await requestLogsHelper.getAllRequestLogsByWorkspaceAndType(
          workspace.id,
          "support"
        );
      setTotalUsedHours(
        (result || []).reduce((sum, item) => sum + (item?.supportHours || 0), 0)
      );
    } catch (error) {
      console.error("An error occurred while loading support logs", error);
    }
  }, [workspace?.id]);

  useEffect(() => {
    loadTotalUsedHours();
  }, [loadTotalUsedHours]);

  const navigateBack = () => setDisplayedView("SUPPORT");

  const adjustWorkspaceUsage = async (delta) => {
    if (!workspace?.id) return null;
    const updatedWorkspace = await workspacesHelper.updateWorkspace(
      workspace.id,
      [
        {
          property: "supportHoursUsed",
          value: Math.max(0, (workspace.supportHoursUsed || 0) + delta),
        },
      ]
    );
    updateWorkspaceInContext(updatedWorkspace);
    return updatedWorkspace;
  };

  const handleSave = async (values) => {
    if (!log?.id || !workspace?.id) return;
    const oldHours = log?.supportHours || 0;
    const { supportHours, description, activityDate } = values;
    const newUsedHours = totalUsedHours - oldHours + supportHours;
    if (newUsedHours > (workspace.supportHours || 0)) {
      AppToasty.show(t("admin.support.exceedsBudget"), {
        type: "danger",
      });
      return;
    }
    setIsSaving(true);
    try {
      const result = await requestLogsHelper.updateSupportLog({
        id: log.id,
        _version: log._version,
        supportHours,
        description,
        creationTime: activityDate
          ? Math.floor(dayjs(activityDate).valueOf() / 1000)
          : Math.floor(dayjs().valueOf() / 1000),
      });
      if (result?.data?.updateRequestLog) {
        await adjustWorkspaceUsage(supportHours - oldHours);
        AppToasty.show(t("admin.support.updateSuccessCaption"), {
          type: "success",
        });
        navigateBack();
      }
    } catch (error) {
      console.error("An error occurred while updating support hours", error);
      AppToasty.show(t("admin.support.updateFailureCaption"), {
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!log?.id || !workspace?.id) return;
    setIsDeleting(true);
    try {
      const result = await requestLogsHelper.deleteSupportLog({
        id: log.id,
        _version: log._version,
      });
      if (result?.data?.deleteRequestLog) {
        await adjustWorkspaceUsage(-(log?.supportHours || 0));
        AppToasty.show(t("admin.support.deleteSuccessCaption"), {
          type: "success",
        });
        navigateBack();
      }
    } catch (error) {
      console.error("An error occurred while deleting support entry", error);
      AppToasty.show(t("admin.support.deleteFailureCaption"), {
        type: "danger",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <SupportEditScreen
      log={log}
      isSaving={isSaving}
      isDeleting={isDeleting}
      onSave={handleSave}
      onDelete={handleDelete}
      navigateBack={navigateBack}
    />
  );
};

export default SupportEditContainer;